import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapPin, Plus, Edit2, Trash2, AlertCircle } from "react-feather";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";

import BaseInput from "../../BaseComponents/BaseInput";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseTooltip from "../../BaseComponents/BaseTooltip";

import { center } from "../../Constant/Inquiry/Inquiry";
import {
  Action,
  Address,
  City,
  Description,
  Location,
  Orgenization,
  Search,
  State,
  Submit,
  SuperAdmin,
  Update,
  notFound,
  organization,
} from "../../Constant";
import { Center, CenterList, DeleteMessage, District, SrNo, pinCode } from "../../Constant/Center";
import { addCenterApi, deleteCenterApi, editCenterApi, listCenterApi } from "../../Api/Center";
import { listOfCity, listOfDistrict, listOfState } from "../../Api/common";
import {
  checkStatusCodeSuccess,
  errHandler,
  getLocalItem,
  handleArrayResponse,
  handleEditClick,
} from "../../Constant/common";
import { centerTitle } from "../../Constant/title";
import { formFields } from "../../Constant/HR_Staff/hrEnums";
import { staffEnums } from "../../Constant/Staff/staff";
import { listOfOrganizationApi } from "../../Api/OrganizationApi";
import { useSelectedOrganization } from "../OrganizationContext";
import { SelectPlaceHolder, validationMessages, zipcodeRegex } from "../../Constant/validation";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { truncateText } from "../../Constant/Lead";
import { useTableQueryParams } from "../../utils/tableUtils";

/* ─── helpers ─────────────────────────────────────────────── */
const TipCell = (val, max = 28, color = "#6366f1") => {
  const t = truncateText(val, max);
  return val && val.length > max ? (
    <BaseTooltip label={val} position="top" maxWidth={280} color={color}>
      <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>{t}</span>
    </BaseTooltip>
  ) : (
    <span>{val || "—"}</span>
  );
};

/* ─── component ───────────────────────────────────────────── */
const CenterPage = () => {
  document.title = centerTitle;

  const auth_role = getLocalItem("role");
  const auth_name = getLocalItem("name");
  const isSuperAdmin = auth_role === SuperAdmin;
  const { selectedOrganization } = useSelectedOrganization();
  const roleOrgId = isSuperAdmin ? selectedOrganization : null;
  const { setLoading } = usePageLoader();

  /* ── table params state ── */
  const {
    currentPage,
    customPageSize,
    searchValue,
    sortOrder,
    columnName,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    resetToFirstPage,
  } = useTableQueryParams({ customPageSize: 5, sortOrder: "DESC" });

  /* ── list state ── */
  const [centerList, setCenterList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);

  /* ── location dropdowns ── */
  const [states, setStates] = useState([]);
  const [district, setDistrict] = useState([]);
  const [city, setCity] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [isDistrictLoading, setIsDistrictLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);

  /* ── modal state ── */
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [centerId, setCenterId] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);

  /* ── fetch org list ── */
  useEffect(() => {
    if (isSuperAdmin) {
      listOfOrganizationApi()
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            setOrganizationList(
              resp?.data?.listOfOrganizations?.map((item) => ({
                value: item?.id,
                label: item?.organization_name,
              })) || [],
            );
          }
        })
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  /* ── fetch states ── */
  const fetchArea = useCallback(() => {
    listOfState({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setStates(
            resp?.data?.map((item) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            })) || [],
          );
        }
      })
      .catch(() => {});
  }, []);

  const fetchDistrict = useCallback((id) => {
    if (!id) {
      setDistrict([]);
      return;
    }
    setIsDistrictLoading(true);
    listOfDistrict({ condition: { state_id: id } })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setDistrict(
            resp?.data?.map((item) => ({
              value: item?.id,
              label: item?.district_name,
              id: item?.id,
            })) || [],
          );
        } else {
          setDistrict([]);
        }
      })
      .catch(() => setDistrict([]))
      .finally(() => setIsDistrictLoading(false));
  }, []);

  const fetchCity = useCallback((id) => {
    if (!id) {
      setCity([]);
      return;
    }
    setIsCityLoading(true);
    listOfCity({ condition: { district_id: id } })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setCity(
            resp?.data?.map((item) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            })) || [],
          );
        } else {
          setCity([]);
        }
      })
      .catch(() => setCity([]))
      .finally(() => setIsCityLoading(false));
  }, []);

  /* ── fetch center list ── */
  const fetchData = useCallback(() => {
    setLoading(true);
    listCenterApi({
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
      search: searchValue,
      ...(roleOrgId && { condition: { organizationId: roleOrgId } }),
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setCenterList(resp?.data?.listOfCenter || []);
          setTotalRecords(resp?.data?.totalRecordsCount || 0);
          setTotalPages(resp?.data?.totalPages || 1);
          setTotalNumberOfRows(resp?.data?.numberOfRows || 0);
        } else {
          setCenterList([]);
        }
      })
      .catch(() => setCenterList([]))
      .finally(() => setLoading(false));
  }, [columnName, sortOrder, currentPage, customPageSize, searchValue, roleOrgId, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchArea();
  }, [fetchArea]);

  /* ── open edit: pre-load district + city ── */
  useEffect(() => {
    if (editData) {
      fetchDistrict(editData?.state?.id);
      fetchCity(editData?.district?.id);
    }
  }, [editData, fetchDistrict, fetchCity]);

  /* ── formik ── */
  const centerForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      center_name: editId !== null ? editData?.center_name : "",
      location: editId !== null ? editData?.location : "",
      district: editId !== null ? editData?.district?.id : null,
      state_name: editId !== null ? editData?.state?.id : null,
      city: editId !== null ? editData?.city?.id : null,
      description: editId !== null ? editData?.description : "",
      address: editId !== null ? editData?.address : "",
      pin_code: editId !== null ? editData?.pin_code : "",
      organization:
        editId !== null
          ? +editData?.organization?.id
          : isSuperAdmin
            ? selectedOrganization || null
            : Number(localStorage.getItem("organizationId")) || null,
    },
    validationSchema: yup.object({
      center_name: yup.string().required(validationMessages.required(center)),
      location: yup.string().required(validationMessages.required(Location)),
      district: yup.string().required(validationMessages.required(District)),
      state_name: yup.string().required(validationMessages.required(State)),
      city: yup.string().required(validationMessages.required(City)),
      address: yup.string().required(validationMessages.required(Address)),
      pin_code: yup
        .string()
        .required(validationMessages.required(pinCode))
        .matches(zipcodeRegex, validationMessages.contactLength(pinCode, 6)),
      organization: isSuperAdmin
        ? yup.string().required(validationMessages.required(Orgenization))
        : yup.string().nullable(),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      const orgId = isSuperAdmin
        ? parseInt(values.organization)
        : Number(localStorage.getItem("organizationId"));

      const payload = {
        organizationId: parseInt(orgId),
        organization_name: auth_name,
        center_name: values.center_name,
        location: values.location,
        district_id: values.district,
        state_id: values.state_name,
        city_id: values.city,
        description: values.description,
        address: values.address,
        pin_code: `${values.pin_code}`,
      };

      const apiCall = editId !== null ? editCenterApi(editId, payload) : addCenterApi(payload);

      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp.message);
            fetchData();
            centerForm.resetForm();
            setShowFormModal(false);
            setEditId(null);
            setEditData(null);
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  /* ── delete ── */
  const deleteCenter = () => {
    setDeleteLoader(true);
    deleteCenterApi(centerId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message);
          if (currentPage === 1) {
            fetchData();
          } else {
            resetToFirstPage();
          }
          setShowDeleteModal(false);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => errHandler(err))
      .finally(() => {
        setDeleteLoader(false);
        setCenterId(null);
      });
  };

  /* ── open add modal ── */
  const openAdd = () => {
    centerForm.resetForm();
    setEditId(null);
    setEditData(null);
    setDistrict([]);
    setCity([]);
    setShowFormModal(true);
  };

  /* ── open edit modal ── */
  const openEdit = (row) => {
    setEditData(row);
    setEditId(row?.id);
    handleEditClick();
    setShowFormModal(true);
  };

  /* ── columns ── */
  const columns = useMemo(() => {
    const base = [
      {
        header: SrNo,
        accessorKey: "serial_number",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        header: center,
        accessorKey: "center_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.center_name, 22, "#6366f1"),
      },
      {
        header: Location,
        accessorKey: "location",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.location, 24, "#8b5cf6"),
      },
      {
        header: Address,
        accessorKey: "address",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.address, 28, "#06b6d4"),
      },
      {
        header: State,
        accessorKey: "state.state_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.state?.state_name, 18, "#10b981"),
      },
      {
        header: District,
        accessorKey: "district.district_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.district?.district_name, 18, "#f59e0b"),
      },
      {
        header: City,
        accessorKey: "city.city_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.city?.city_name, 18, "#ec4899"),
      },
    ];

    if (isSuperAdmin) {
      base.push({
        header: organization,
        accessorKey: "organization.organization_name",
        enableColumnFilter: false,
        cell: (c) => TipCell(c.row.original?.organization?.organization_name, 22, "#6366f1"),
      });
    }

    base.push({
      header: Action,
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rec = row.original;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BaseTooltip label="Edit Center" position="top">
              <button
                type="button"
                onClick={() => openEdit(rec)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: "rgba(245,158,11,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(245,158,11,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(245,158,11,0.12)";
                }}
              >
                <Edit2 size={14} strokeWidth={2} style={{ color: "#f59e0b" }} />
              </button>
            </BaseTooltip>

            <BaseTooltip label="Delete Center" position="top">
              <button
                type="button"
                onClick={() => {
                  setCenterId(rec.id);
                  setShowDeleteModal(true);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  background: "rgba(244,63,94,0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(244,63,94,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(244,63,94,0.12)";
                }}
              >
                <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
              </button>
            </BaseTooltip>
          </div>
        );
      },
    });

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, currentPage, customPageSize]);

  /* ── render ── */
  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page header */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MapPin size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {Center}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0
                ? `${totalRecords} ${CenterList.toLowerCase()}`
                : "Manage your centers"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={openAdd}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> Add Center
        </BaseButton>
      </div>

      {/* Table */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {centerList.length > 0 || searchValue !== "" ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={handlePageChange}
            currentPage={currentPage}
            fetchData={handlePageChange}
            customPageSize={customPageSize}
            setCustomPageSize={handlePageSizeChange}
            columns={columns}
            data={centerList}
            isGlobalFilter={true}
            manualPagination={true}
            manualFiltering={true}
            onSearch={handleSearchChange}
            fetchSortingData={handleSortChange}
            SearchPlaceholder={Search}
            shouldLineBreak={false}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      <BaseModal
        isOpen={showFormModal}
        toggler={() => {
          if (!btnLoader) {
            centerForm.resetForm();
            setEditId(null);
            setEditData(null);
            setShowFormModal(false);
          }
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editId !== null ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>{editId !== null ? "Edit Center" : "Add Center"}</span>
          </div>
        }
        submitText={editId !== null ? Update : Submit}
        disabled={btnLoader}
        loader={btnLoader}
        submit={centerForm.handleSubmit}
        size="lg"
      >
        <form onSubmit={centerForm.handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "0 16px",
            }}
          >
            {/* Center Name */}
            <BaseInput
              name="center_name"
              type="text"
              label={center}
              placeholder={PlaceHolderFormat(center)}
              value={centerForm.values.center_name}
              touched={centerForm.touched.center_name}
              error={centerForm.errors.center_name}
              handleBlur={centerForm.handleBlur}
              handleChange={centerForm.handleChange}
              required
            />

            {/* Location */}
            <BaseInput
              name="location"
              type="text"
              label={Location}
              placeholder={PlaceHolderFormat(Location)}
              value={centerForm.values.location}
              touched={centerForm.touched.location}
              error={centerForm.errors.location}
              handleBlur={centerForm.handleBlur}
              handleChange={centerForm.handleChange}
              required
            />

            {/* State */}
            <BaseSelect
              name="state_name"
              options={states}
              label={State}
              placeholder={SelectPlaceHolder(State)}
              value={centerForm.values.state_name}
              touched={centerForm.touched.state_name}
              error={centerForm.errors.state_name}
              handleBlur={() => centerForm.setFieldTouched("state_name", true)}
              handleChange={(field, value) => {
                centerForm.setFieldValue(field, value);
                centerForm.setFieldValue("district", null);
                centerForm.setFieldValue("city", null);
                fetchDistrict(value);
                setCity([]);
              }}
              required
            />

            {/* District */}
            <BaseSelect
              name="district"
              options={district}
              label={District}
              placeholder={SelectPlaceHolder(District)}
              value={centerForm.values.district}
              touched={centerForm.touched.district}
              error={centerForm.errors.district}
              handleBlur={() => centerForm.setFieldTouched("district", true)}
              handleChange={(field, value) => {
                centerForm.setFieldValue(field, value);
                centerForm.setFieldValue("city", null);
                fetchCity(value);
              }}
              isLoading={isDistrictLoading}
              required
            />

            {/* City */}
            <BaseSelect
              name="city"
              options={city}
              label={City}
              placeholder={SelectPlaceHolder(City)}
              value={centerForm.values.city}
              touched={centerForm.touched.city}
              error={centerForm.errors.city}
              handleBlur={() => centerForm.setFieldTouched("city", true)}
              handleChange={(field, value) => centerForm.setFieldValue(field, value)}
              isLoading={isCityLoading}
              required
            />

            {/* Pin Code */}
            <BaseInput
              name="pin_code"
              type="number"
              label={pinCode}
              placeholder={PlaceHolderFormat(pinCode)}
              value={centerForm.values.pin_code}
              touched={centerForm.touched.pin_code}
              error={centerForm.errors.pin_code}
              handleBlur={centerForm.handleBlur}
              handleChange={centerForm.handleChange}
              required
            />

            {/* Organization (SuperAdmin only) */}
            {isSuperAdmin && (
              <BaseSelect
                id={formFields.organization}
                name={formFields.organization}
                label={staffEnums.ORGANIZATION}
                options={organizationList}
                placeholder={SelectPlaceHolder(staffEnums.ORGANIZATION)}
                value={centerForm.values.organization}
                touched={centerForm.touched.organization}
                error={centerForm.errors.organization}
                handleBlur={() => centerForm.setFieldTouched("organization", true)}
                handleChange={(field, selected) =>
                  centerForm.setFieldValue(field, selected ?? null)
                }
                isDisabled={isSuperAdmin && !!selectedOrganization}
                required
              />
            )}

            {/* Address — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <BaseInput
                name="address"
                type="textarea"
                label={Address}
                rows={2}
                placeholder={PlaceHolderFormat(Address)}
                value={centerForm.values.address}
                touched={centerForm.touched.address}
                error={centerForm.errors.address}
                handleBlur={centerForm.handleBlur}
                handleChange={centerForm.handleChange}
                required
              />
            </div>

            {/* Description — full width */}
            <div style={{ gridColumn: "1 / -1" }}>
              <BaseInput
                name="description"
                type="textarea"
                label={Description}
                placeholder={PlaceHolderFormat(Description)}
                value={centerForm.values.description}
                touched={centerForm.touched.description}
                error={centerForm.errors.description}
                handleBlur={centerForm.handleBlur}
                handleChange={centerForm.handleChange}
              />
            </div>
          </div>
        </form>
      </BaseModal>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={showDeleteModal}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(244,63,94,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash2 size={14} strokeWidth={2} style={{ color: "#f43f5e" }} />
            </div>
            <span>Delete Center</span>
          </div>
        }
        toggler={() => {
          if (!deleteLoader) {
            setShowDeleteModal(false);
            setCenterId(null);
          }
        }}
        submit={deleteCenter}
        submitText="Delete"
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteMessage}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default CenterPage;
